import { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search, Plus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

interface Option {
    value: string;
    label: string;
}

interface SearchableSelectProps {
    value: string | undefined;
    onValueChange: (value: string) => void;
    options: Option[];
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
    /**
     * Quando definido, exibe um botão "Criar 'XYZ'" no topo do dropdown
     * sempre que o texto de busca não bater com nenhuma label existente
     * (case-insensitive). O callback recebe o nome digitado e deve retornar
     * uma Promise com a Option recém-criada (`{ value, label }`) — daí
     * selecionamos automaticamente e fechamos o popover.
     */
    onCreate?: (name: string) => Promise<Option>;
    /** Label do botão criar. Default: "Criar". */
    createLabel?: string;
}

export function SearchableSelect({
    value,
    onValueChange,
    options,
    placeholder = 'Selecione...',
    searchPlaceholder = 'Buscar...',
    className,
    onCreate,
    createLabel = 'Criar',
}: SearchableSelectProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [creating, setCreating] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const selected = options.find((o) => o.value === value);

    const filtered = options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
    );

    const trimmedSearch = search.trim();
    const exactMatch = filtered.some(
        (o) => o.label.toLowerCase() === trimmedSearch.toLowerCase()
    );
    // Mostra opção "Criar" quando: tem callback + algo digitado + sem match exato.
    const canCreate = !!onCreate && trimmedSearch.length > 0 && !exactMatch;

    const handleCreate = async () => {
        if (!onCreate || !trimmedSearch) return;
        setCreating(true);
        try {
            const newOption = await onCreate(trimmedSearch);
            onValueChange(newOption.value);
            setOpen(false);
        } catch {
            // Erro tratado pelo consumer (toast). Mantemos popover aberto.
        } finally {
            setCreating(false);
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && canCreate && !creating) {
            e.preventDefault();
            void handleCreate();
        }
    };

    useEffect(() => {
        if (open) {
            setSearch('');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        'w-full justify-between font-normal rounded-xl border-gray-200 bg-white hover:bg-gray-50',
                        !selected && 'text-gray-400',
                        className
                    )}
                >
                    <span className="truncate">{selected ? selected.label : placeholder}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                </Button>
            </PopoverTrigger>

            <PopoverContent
                className="p-0 rounded-xl border border-gray-100 shadow-xl bg-white"
                style={{ width: 'var(--radix-popover-trigger-width)' }}
                align="start"
                // Dentro de Dialog do Radix, o body fica com pointer-events: none
                // e o wheel é interceptado. Isolamos o popover.
                onWheelCapture={(e) => e.stopPropagation()}
                onTouchMoveCapture={(e) => e.stopPropagation()}
            >
                {/* Search input */}
                <div className="flex items-center border-b border-gray-100 px-3 py-2 gap-2">
                    <Search className="h-4 w-4 shrink-0 text-gray-400" />
                    <input
                        ref={inputRef}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={searchPlaceholder}
                        className="flex-1 text-sm outline-none bg-transparent placeholder:text-gray-400"
                    />
                </div>

                {/* Options list */}
                <div className="max-h-60 overflow-y-auto py-1">
                    {/* Create option — aparece no topo quando há texto sem match exato */}
                    {canCreate && (
                        <button
                            onClick={() => void handleCreate()}
                            disabled={creating}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors
                                       hover:bg-emerald-50 text-emerald-700 border-b border-gray-100 disabled:opacity-60"
                        >
                            {creating ? (
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                            ) : (
                                <Plus className="h-4 w-4 shrink-0" />
                            )}
                            <span className="truncate">
                                {createLabel} <strong>"{trimmedSearch}"</strong>
                            </span>
                        </button>
                    )}

                    {filtered.length === 0 && !canCreate ? (
                        <p className="px-4 py-6 text-center text-sm text-gray-400">
                            Nenhum resultado encontrado.
                        </p>
                    ) : (
                        filtered.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => {
                                    onValueChange(option.value);
                                    setOpen(false);
                                }}
                                className={cn(
                                    'w-full flex items-center justify-between px-4 py-2 text-sm text-left transition-colors hover:bg-gray-50',
                                    option.value === value && 'bg-blue-50 text-blue-700 font-medium'
                                )}
                            >
                                <span>{option.label}</span>
                                {option.value === value && (
                                    <Check className="h-4 w-4 text-blue-600 shrink-0" />
                                )}
                            </button>
                        ))
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
